import httpClient, { ConexaResponse } from '@/services/httpClient'
import {
  Filters,
  Cities,
  ProfilesPsyAliveCards,
  PsychologistDay,
  PsychologistDayResponse,
  PsychologistsDataResponse,
  GetPsyAliveListData,
  UsePsyScheduleParams,
  PsyAliveListResponse,
  DaySlotSchedule,
  GetPsychologistProfileV2Params,
  ProfessionalProfileType,
  AverageRatingType,
  ProfessionalReviewList,
} from './types'
import { PsyAliveFilterStore } from './store/PsyAliveFilterStore/types'
import {
  IPsyAliveRating,
  ProfessionalComments,
} from '../Scheduled/scheduled-types'
import ScreeningProfessionalListMapper from './mappers/ScreeningProfessionalListMapper/ScreeningProfessionalListMapper'
import PsyAliveListMapper from './mappers/PsyAliveListMapper/PsyAliveListMapper'
import PsyScheduleMapper from './mappers/PsyScheduleMapper/PsyScheduleMapper'
import FiltersNewSearch from './mappers/FiltersNewSearch/FiltersNewSearch'
import qs from 'qs'
import { autoCapitalizeName } from '@/utils/autoCapitalizeName'

export const getFilters = async (): Promise<Filters> => {
  const response = await httpClient.get('/pv/psicologos/filtros/v1')

  return response?.data?.object
}

export const getCities = async ({
  id,
}: {
  id: string | number
}): Promise<Cities[]> => {
  const response = await httpClient.get('/pv/psicologos/filtros/v1/cidades', {
    params: { id },
  })

  return (
    response.data.object?.map(item => {
      return {
        value: item.key,
        label: item.value,
      }
    }) || []
  )
}

export async function updateFilterPsyAlive(
  data: Omit<
    PsyAliveFilterStore,
    'getFilterData' | 'updatePsyAliveFilter' | 'resetFormStore'
  >,
): Promise<PsychologistsDataResponse> {
  const requestData = {
    idPatient: data?.idPatient,
    tema: data.themes?.value,
    area: data.groupArea?.value,
    especialidade: data.specialties?.value,
    genero: data.sex?.value,
    estado: data.states?.id,
    cidade: data.cities?.value,
    faixasEtaria: data.ageGroups?.value,
    atendeLibras: data.providesSignLanguageService,
    disponibilidadeInicio: data.availabilityStart,
    disponibilidadeFim: data.availabilityEnd,
    abordagens: data.approaches?.value,
    nomeDoProfissional: autoCapitalizeName(data?.professionalName),
    page: data?.page ?? 1,
    executeCount: false,
    rowsPerPage: 10,
  }

  const response = await httpClient.get('/pv/psicologos/v2', {
    params: requestData,
  })
  return ScreeningProfessionalListMapper.toDomain(response?.data?.object)
}

export const getPsychologyProfessionalDetails = async ({
  ids,
}: {
  ids: number[]
}): Promise<ProfilesPsyAliveCards[]> => {
  const response = await httpClient.get('/pv/psicologos/v1/detalhe', {
    params: { idPsicologos: ids.join(',') },
  })

  return response.data.object?.items
}

export const getPsychologistDays = async (
  professionalId: string,
  month: string,
  year: string,
): Promise<PsychologistDay[]> => {
  const response = await httpClient.get<
    ConexaResponse<PsychologistDayResponse[]>
  >('/pv/psicologos/v1/agenda', {
    params: {
      psicologos: professionalId,
      mes: month.length === 2 ? month : '0' + month,
      ano: year,
    },
  })

  const psychologistDays: PsychologistDay[] = response?.data?.object?.map(
    day => {
      return {
        date: day?.data,
        schedule: {
          dawn: day?.horarios?.MADRUGADA,
          morning: day?.horarios?.MANHA,
          afternoon: day?.horarios?.TARDE,
          night: day?.horarios?.NOITE,
        },
      }
    },
  )

  return psychologistDays
}
export const getRatingPsyAliveProfessional = async (
  id: string,
  page: number,
): Promise<IPsyAliveRating[]> => {
  return httpClient
    .get(`/pv/psicologos/v1/avaliacoes/${id}`, {
      params: {
        page: page,
        rowsPerPage: 10,
        descending: true,
      },
    })
    .then(res => {
      return res.data.object.items?.map(rating => {
        return {
          id: rating.id,
          dataUltAlteracao: rating.dataUltAlteracao,
          exibirPerfilPublico: rating.exibirPerfilPublico,
          nomePaciente: rating.nomePaciente,
          pontos: rating.pontos,
          observacao: rating.observacao,
        }
      })
    })
}

export const getProfessionalComments = async (
  clinicId: number,
  professionalId: number,
  page = 1,
) => {
  const response = await httpClient.get<ConexaResponse<ProfessionalComments[]>>(
    `/v1/clinics/${clinicId}/professionals/${professionalId}/comments`,
    {
      params: {
        page,
        limit: 10,
      },
    },
  )

  return response?.data?.object || []
}

export const checkAsyncPsyAlive = async (): Promise<boolean> => {
  const response = await httpClient.put(`/pv/paciente`)

  return response?.data?.object
}

export const getScreeningProfessionalList = async (
  patientId: number,
  pageParam: number,
): Promise<PsychologistsDataResponse> => {
  const {
    data: { object },
  } = await httpClient.get(`/pv/psicologos/v2`, {
    params: {
      idPatient: patientId,
      page: pageParam,
      executeCount: true,
      rowsPerPage: 10,
    },
  })

  return ScreeningProfessionalListMapper.toDomain(object)
}

export const disconnectScreeningProfessional = async (
  patientId: number,
): Promise<void> => {
  await httpClient.post(`/v2/linkPatientProfessional/${patientId}/unlink`)
}

export const getScreeningForwarded = async (
  patientId: number,
): Promise<boolean> => {
  const response = await httpClient.get(
    `/v2/linkPatientProfessional/${patientId}/hasActiveLink`,
  )

  return response?.data?.object
}

export const getThemesPsyAliveProfessional = async (
  id: number,
): Promise<string[]> => {
  return httpClient.get(`/v1/theme/${id}`).then(response => {
    return response?.data?.object
  })
}

export const getPsyAliveList = async (
  data: GetPsyAliveListData,
  isPsychologistAlreadyMigrated: boolean,
) => {
  const requestData = PsyAliveListMapper.toPersistence(
    data,
    isPsychologistAlreadyMigrated,
  )

  const {
    data: { object },
  } = await httpClient.get<ConexaResponse<PsyAliveListResponse>>(
    `v1/clinics/${data.clinicId}/professionals`,
    {
      params: requestData,
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      },
    },
  )

  const responseMapped = {
    total: object?.total,
    items: object?.items?.map(PsyAliveListMapper.toDomain),
  }

  return responseMapped
}

export const getPsychologistSchedule = async (params: UsePsyScheduleParams) => {
  const {
    data: { object },
  } = await httpClient.get<ConexaResponse<DaySlotSchedule[]>>(
    `v1/clinics/${params.clinicId}/professionals/${params.professionalId}?startDate=${params.startDate}&endDate=${params.endDate}`,
    {
      params: {
        isPsychologistAlreadyMigrated: params?.isPsychologistAlreadyMigrated,
      },
    },
  )

  return object.map(PsyScheduleMapper.toDomain)
}

export const getFiltersNewSearch = async () => {
  const response = await httpClient.get<ConexaResponse<Filters>>(
    '/pv/psicologos/filtros/v1',
  )

  return FiltersNewSearch.toDomain(response?.data?.object)
}

export const getPsychologistAlreadyMigrated = async (
  professionalType: string,
) => {
  const response = await httpClient.get(
    `/professionals/search/slot-schedule/can-show/${professionalType}`,
  )

  return response?.data?.object
}

export const getPsychologistProfileV2 = async ({
  clinicId,
  professionalId,
  noScheduleAvailable = false,
}: GetPsychologistProfileV2Params) => {
  const response = await httpClient.get<ConexaResponse<PsyAliveListResponse>>(
    `v1/clinics/${clinicId}/professionals/${professionalId}/specialty`,
    {
      params: {
        isPsychologistAlreadyMigrated: true,
        noScheduleAvailable: noScheduleAvailable,
      },
    },
  )

  return PsyAliveListMapper.toDomain(response?.data?.object?.items[0])
}

export const getProfessionalIsNotified = async (professionalId: number) => {
  const response = await httpClient.get<ConexaResponse<boolean>>(
    `/integration/app-paciente/profissional/notificacao-novos-horarios/${professionalId}/notificado`,
  )

  return response?.data?.object
}

export const checkProfessionalAvailability = async (
  professionalId: number,
): Promise<boolean> => {
  const response = await httpClient.get<ConexaResponse<boolean>>(
    `/integration/app-paciente/profissional/agenda/disponivel?idProfissional=${professionalId}`,
  )

  return response.data.object
}

export const requestScheduleNotification = async (professionalId: number) => {
  const response = await httpClient.post<ConexaResponse<boolean>>(
    `/integration/app-paciente/profissional/solicitar-horario?idProfissional=${professionalId}`,
  )

  return response.data.object
}

export const getProfessionalProfile = async (professionalId: string) => {
  const response = await httpClient.get<
    ConexaResponse<ProfessionalProfileType>
  >(
    `integration/app-paciente/profissional/detalhar/${professionalId}/perfil-publico`,
  )

  return response?.data?.object
}

export const getAverageRatingScore = async (professionalId: string) => {
  const response = await httpClient.get<ConexaResponse<AverageRatingType>>(
    `/integration/app-paciente/profissional/detalhar/${professionalId}/perfil-publico/media-avaliacoes`,
  )

  return response?.data?.object
}

export const getProfessionalReviews = async (
  professionalId: string,
  page: number,
  size: number,
) => {
  const response = await httpClient.get<ConexaResponse<ProfessionalReviewList>>(
    `/integration/app-paciente/profissional/detalhar/${professionalId}/perfil-publico/avaliacoes`,
    {
      params: {
        page,
        size,
      },
    },
  )
  return response?.data?.object
}

export const checkProfessionalSlots = async (
  slotId: string | null,
  professionalId: string | number,
  date: string,
) => {
  const response = await httpClient.post<ConexaResponse<boolean>>(
    `/integration/app-paciente/atendimento/slots/profissional/${professionalId}/reservar`,
    {
      slotId,
      data: date,
    },
  )

  return response.data.object
}
